// First Created - 26 May 2026
// Author - Atul Pandey
// Purpose - To create a reusable Session QNA question set for Trinity Sessions

import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import PulseLoader from "react-spinners/PulseLoader"
import EtContentEditorLightWithPlace from "@/utils/EtContentEditorLightWithPlace"
import EtContentEditorLight3 from "@/utils/EtContentEditorLight3"
import { PlusIcon, MinusIcon } from "@heroicons/react/24/outline"

import { useCreateNewSessionQNAMutation } from "./sessionQNAsApiSlice"

const EtCreateSessionQNA = ({ setActiveAdminView }) => {
  // First Created - 26 May 2026
  // Author - Atul Pandey
  // Purpose - To create a reusable Session QNA question set for Trinity Sessions

  useTitle("Create Session QNA")

  const navigate = useNavigate()

  const [createNewSessionQNA, { isLoading, isSuccess, isError, error }] =
    useCreateNewSessionQNAMutation()

  const initialFormState = {
    sessionQNAName: "",
    sessionQNADesc: "",
    sessionQNAIntro: "",
    sessionQNAOutro: "",
    sessionQNAQuestions: [
      {
        questionText: "",
        questionDesc: "",
        questionSequence: 1,
        questionType: "Introspection",
        questionPrompt: "",
        active: true,
      },
    ],
    aboutMe: "",
    pic: "",
    active: true,
  }

  const [formData, setFormData] = useState(initialFormState)

  useEffect(() => {
    if (isSuccess) {
      setFormData(initialFormState)

      if (setActiveAdminView) {
        setActiveAdminView("sessionQNAs")
      } else {
        navigate("/sessionQNAs")
      }
    }
  }, [isSuccess])

  const handleChange = e => {
    const { name, value, files, type, checked } = e.target

    if (name === "pic") {
      setFormData(prevState => ({ ...prevState, pic: files[0] }))
    } else if (type === "checkbox") {
      setFormData(prevState => ({ ...prevState, [name]: checked }))
    } else {
      setFormData(prevState => ({ ...prevState, [name]: value }))
    }
  }

  const handleQuestionChange = (index, key, value) => {
    const updatedQuestions = formData.sessionQNAQuestions.map((question, i) =>
      i === index ? { ...question, [key]: value } : question
    )

    setFormData({
      ...formData,
      sessionQNAQuestions: updatedQuestions,
    })
  }

  const handleAddQuestion = () => {
    setFormData(prevState => ({
      ...prevState,
      sessionQNAQuestions: [
        ...prevState.sessionQNAQuestions,
        {
          questionText: "",
          questionDesc: "",
          questionSequence: prevState.sessionQNAQuestions.length + 1,
          questionType: "Introspection",
          questionPrompt: "",
          active: true,
        },
      ],
    }))
  }

  const handleRemoveQuestion = index => {
    const updatedQuestions = formData.sessionQNAQuestions
      .filter((_, i) => i !== index)
      .map((question, questionIndex) => ({
        ...question,
        questionSequence: questionIndex + 1,
      }))

    setFormData(prevState => ({
      ...prevState,
      sessionQNAQuestions: updatedQuestions,
    }))
  }

  const handleSubmit = async e => {
    e.preventDefault()

    const cleanedQuestions = formData.sessionQNAQuestions
      .filter(question => question.questionText?.trim())
      .map((question, index) => ({
        questionText: question.questionText,
        questionDesc: question.questionDesc,
        questionSequence: question.questionSequence || index + 1,
        questionType: question.questionType || "Introspection",
        questionPrompt: question.questionPrompt,
        active: question.active !== false,
      }))

    const sessionQNAData = {
      sessionQNAName: formData.sessionQNAName,
      sessionQNADesc: formData.sessionQNADesc,
      sessionQNAIntro: formData.sessionQNAIntro,
      sessionQNAOutro: formData.sessionQNAOutro,
      sessionQNAQuestions: cleanedQuestions,
      aboutMe: formData.aboutMe,
      active: formData.active,
    }

    if (formData.pic) {
      const reader = new FileReader()

      reader.onloadend = async () => {
        sessionQNAData.pic = reader.result

        try {
          await createNewSessionQNA(sessionQNAData).unwrap()
        } catch (error) {
          console.error(error)
        }
      }

      reader.readAsDataURL(formData.pic)
    } else {
      try {
        await createNewSessionQNA(sessionQNAData).unwrap()
      } catch (error) {
        console.error(error)
      }
    }
  }

  const handleCancel = () => {
    if (setActiveAdminView) {
      setActiveAdminView("sessionQNAs")
    } else {
      navigate("/sessionQNAs")
    }
  }

  const handleClear = () => {
    setFormData(initialFormState)
  }

  return (
    <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black">
      {isLoading && <PulseLoader color={"#ea580c"} />}

      {isError && (
        <p className="text-orange-700 font-semi-bold py-2">
          {error?.data?.message}
        </p>
      )}

      <div className="bg-white p-1 m-1 rounded">
        <p className="p-1 m-1 font-bold font-poppins text-xl">
          Add a Session QNA
        </p>

        <div className="bg-gray-200 rounded m-1 p-1">
          <form onSubmit={handleSubmit} className="m-1 p-1 text-xs font-poppins">

            {/* 1 - Basic QNA Set Fields */}
            <div id="sessionQNAName">
              <input
                type="text"
                name="sessionQNAName"
                placeholder="Enter the Session QNA name here"
                value={formData.sessionQNAName}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>

            <div id="sessionQNADesc">
              <input
                type="text"
                name="sessionQNADesc"
                placeholder="Enter the Session QNA description here"
                value={formData.sessionQNADesc}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>

            {/* 2 - Intro */}
            <div className="bg-white w-full rounded p-2 m-1">
              <EtContentEditorLightWithPlace
                value={formData.sessionQNAIntro}
                handleChange={content =>
                  setFormData({ ...formData, sessionQNAIntro: content })
                }
                placeholderText={"Write the opening context for this QNA set here"}
              />
            </div>

            {/* 3 - Outro */}
            <div className="bg-white w-full rounded p-2 m-1">
              <EtContentEditorLight3
                value={formData.sessionQNAOutro}
                handleChange={content =>
                  setFormData({ ...formData, sessionQNAOutro: content })
                }
                placeholderText={"Write the closing context for this QNA set here"}
                toolbarId={"sessionQNAOutro"}
              />
            </div>

            {/* 4 - Questions */}
            <div className="m-1 p-1 border border-white rounded">
              <div>
                <p className="font-bold mx-2 px-2 mt-2">Questions</p>
                <p className="text-[8px] mx-2 px-2 mb-2 italic text-gray-600">
                  Add the questions that users and coaches will respond to during the session.
                </p>
              </div>

              {formData.sessionQNAQuestions.map((question, index) => (
                <div key={index} className="bg-white rounded m-1 p-1">

                  <div className="grid grid-cols-12 gap-1 items-center mx-1">
                    <input
                      type="number"
                      value={question.questionSequence}
                      onChange={e =>
                        handleQuestionChange(
                          index,
                          "questionSequence",
                          Number(e.target.value)
                        )
                      }
                      placeholder="Seq"
                      className="col-span-1 rounded p-2 m-1 text-xs bg-gray-100"
                    />

                    <input
                      type="text"
                      value={question.questionText}
                      onChange={e =>
                        handleQuestionChange(index, "questionText", e.target.value)
                      }
                      placeholder="Question text"
                      className="col-span-6 rounded p-2 m-1 text-xs bg-gray-100"
                    />

                    <select
                      value={question.questionType}
                      onChange={e =>
                        handleQuestionChange(index, "questionType", e.target.value)
                      }
                      className="col-span-4 rounded p-2 m-1 text-xs bg-gray-100"
                    >
                      <option value="Reflection">Reflection</option>
                      <option value="Introspection">Introspection</option>
                      <option value="Discussion">Discussion</option>
                      <option value="Action">Action</option>
                      <option value="Other">Other</option>
                    </select>

                    <div className="col-span-1 rounded bg-red-500 m-1 p-1 flex justify-center">
                      <button
                        type="button"
                        onClick={() => handleRemoveQuestion(index)}
                        disabled={formData.sessionQNAQuestions.length === 1}
                      >
                        <MinusIcon className="h-5 w-5 text-white" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-12 gap-1 items-center mx-1">
                    <input
                      type="text"
                      value={question.questionDesc}
                      onChange={e =>
                        handleQuestionChange(index, "questionDesc", e.target.value)
                      }
                      placeholder="Question description"
                      className="col-span-6 rounded p-2 m-1 text-xs bg-gray-100"
                    />

                    <input
                      type="text"
                      value={question.questionPrompt}
                      onChange={e =>
                        handleQuestionChange(index, "questionPrompt", e.target.value)
                      }
                      placeholder="Prompt or hint for users"
                      className="col-span-5 rounded p-2 m-1 text-xs bg-gray-100"
                    />

                    <div className="col-span-1 flex justify-center items-center">
                      <input
                        type="checkbox"
                        checked={question.active}
                        onChange={e =>
                          handleQuestionChange(index, "active", e.target.checked)
                        }
                        className="m-1"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <div className="flex justify-start items-center m-1">
                <p className="font-bold text-xs m-1 p-1">Add Question</p>
                <button type="button" onClick={handleAddQuestion}>
                  <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-green-500" />
                </button>
              </div>
            </div>

            {/* 5 - About / Pic / Active */}
            <div id="aboutMe">
              <input
                type="text"
                name="aboutMe"
                placeholder="Add internal notes about this QNA set"
                value={formData.aboutMe}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>

            <div id="pic">
              <input
                type="file"
                name="pic"
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>

            <div className="flex justify-start items-center bg-white rounded m-1 p-2">
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleChange}
                className="m-1"
              />
              <p className="text-xs m-1">Active</p>
            </div>

            {/* 6 - Actions */}
            <div id="actions" className="bg-white m-1 px-1 py-3 rounded">
              <EtBtnFormBasic
                name={"Create"}
                type={"create"}
                handleClick={handleSubmit}
              />

              <EtBtnFormBasic
                name={"Clear"}
                type={"clear"}
                handleClick={handleClear}
              />

              <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={handleCancel}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EtCreateSessionQNA